import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plane, Eye, EyeOff, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, providerLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let user = null;
    let firstError = null;
    // 1) Try admin/gestor login first.
    try {
      user = await login(email, password);
    } catch (err) {
      firstError = err;
      // 2) If that fails with invalid creds, fall back to provider login
      //    so a single form covers admin/gestor/provider entry.
      const status = err.response?.status;
      if (status === 401 || status === 404) {
        try {
          user = await providerLogin(email, password);
        } catch {
          // fallthrough — show original error
        }
      }
    }

    if (user) {
      toast.success(`¡Bienvenido ${user.name}!`);
      if (user.role === 'payment_agent' || user.role === 'ticket_provider') {
        navigate(user.force_password_change ? '/provider/cuenta' : '/provider');
      } else if (user.role === 'gestor') {
        navigate('/remesas');
      } else {
        navigate('/');
      }
    } else {
      const message = firstError?.response?.data?.detail || 'Credenciales incorrectas';
      toast.error(message, { duration: 6000 });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-slate-900/90" />
      
      <div className="relative w-full max-w-md">
        {/* Glass card */}
        <div className="glass rounded-2xl shadow-2xl p-8 animate-fadeIn">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl mb-4 shadow-lg overflow-hidden ring-2 ring-amber-300">
              <img src="/aircom-logo.png" alt="Aircom Connect" className="w-full h-full object-contain p-1" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Aircom Connect
            </h1>
            <p className="text-slate-500 mt-1">Iniciar Sesión</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                className="bg-slate-50 border-slate-200 h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contraseña asignada"
                  required
                  className="bg-slate-50 border-slate-200 h-11 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-700 hover:bg-blue-800 text-white font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : 'Iniciar sesión'}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-6 p-3 bg-slate-50 rounded-lg text-sm text-slate-600 text-center">
            Gestores, proveedores y administradores entran desde acá con su correo y contraseña asignada.
          </div>

          <div className="mt-3 text-center">
            <Link to="/cliente/login" className="block text-sm text-blue-500 hover:text-blue-600 font-medium" data-testid="go-to-client-portal">
              Ir al Portal de Clientes
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/60 text-sm mt-6">
          © 2024 Aircom Connect. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
